<script setup lang="ts">
import { computed } from 'vue';
import { Icon } from '@iconify/vue';
/**
 * For @nuxt/icons with flowbite-icons, to be able to include bundling of
 * icons, it is recommended to include the prefix to the `name` prop like
 * `flowbite:icon-name` and @nuxt/icons will take care of bundling those
 * icons. Prefixes includes: 'flowbite', 'logo', 'flag', 'social'
 *
 * Ex: <IconSvg name="spx:player"
 * Ex: <IconSvg name="flowbite:user-solid" />
 * Ex: <IconSvg name="logo:assets-lockup" />
 * Ex: <IconSvg name="flag:ad" />
 * Ex: <IconSvg name="social:euro" />
 *
 * See: https://github.com/nuxt/icon?tab=readme-ov-file#scan-components
 */

const SVG_LOGO_SIZES = [
  'xs',
  'sm',
  'default',
  'lg',
  'xl'
];

const props = defineProps({
  name: {
    type: String,
    required: true
  },
  widthSize: {
    type: String,
    validator(value: string) {
      return ['xs', 'sm', 'default', 'lg', 'xl'].includes(value);
    }
  },
  heightSize: {
    type: String,
    default: 'default',
    validator(value: string) {
      return ['xs', 'sm', 'default', 'lg', 'xl'].includes(value);
    }
  }
});

const svgIconType = computed<string | null>(() => {
  if (!props.name) return null;

  // Get the icon type from the string before the `:` from props.name
  return props.name.match(/^[^:]+/)?.[0] ?? null;
});

const svgIconName = computed<string | null>(() => {
  if (!props.name) return null;

  // Get the icon name from the string after the `:` from props.name
  return props.name.match(/:(.+)/)?.[0] ?? null;
});

const svgIconWidthSize = computed<string | null | undefined>(() => {
  return SVG_LOGO_SIZES.includes(props.widthSize || '') ? props.widthSize : null;
});

const svgIconHeightSize = computed<string | null | undefined>(() => {
  return SVG_LOGO_SIZES.includes(props.heightSize || '') ? props.heightSize : null;
});

/**
 * Customise custom icons by replacing static fill and stroke colors
 * into `currentColor`
 */
const customise = (content: string, name: string, prefix: string) => {
  if (prefix !== 'spx') return content;

  const newContent = content
    .replace(/fill="[^"]*"/g, `fill="currentColor"`)
    .replace(/stroke="[^"]*"/g, `stroke="currentColor"`);

  return newContent;
};
</script>

<template>
  <Icon
    v-if="svgIconName && svgIconType"
    v-bind="svgIconType === 'logo' ? { width: 'unset', height: 'unset' } : ''"
    class="svg-icon"
    :icon="name"
    :customise="customise"
    :class="{
      'w-6': svgIconWidthSize === 'xs',
      'w-8': svgIconWidthSize === 'sm',
      'w-12': svgIconWidthSize === 'default',
      'w-16': svgIconWidthSize === 'lg',
      'w-24': svgIconWidthSize === 'xl',
      'h-6': svgIconHeightSize === 'xs',
      'h-8': svgIconHeightSize === 'sm',
      'h-12': svgIconHeightSize === 'default',
      'h-16': svgIconHeightSize === 'lg',
      'h-24': svgIconHeightSize === 'xl',
    }"
  />
</template>

<style lang="css" scoped>
.svg-icon {
  display: block;
  font-size: 24px;
}
</style>
